// src/pages/dashboard/WidgetConfig.js
// Booking widget configuration panel — customize and copy embed code.

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Copy, Check, ChevronDown } from 'lucide-react';
import { auth, db } from '../../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { trackEvent } from '../../utils/analytics';

// ---------------------------------------------------------------------------
// Text presets
// ---------------------------------------------------------------------------

const TEXT_PRESETS = [
  'Book a Session',
  'Schedule Now',
  'Work With Me',
  'Book Now',
];

// ---------------------------------------------------------------------------
// WidgetConfig page
// ---------------------------------------------------------------------------

const WidgetConfig = () => {
  const navigate = useNavigate();

  // Practitioner data
  const [slug, setSlug] = useState(null);
  const [loading, setLoading] = useState(true);

  // Widget settings
  const [buttonText, setButtonText] = useState('Book a Session');
  const [isCustomText, setIsCustomText] = useState(false);
  const [buttonColor, setButtonColor] = useState('#7c3aed');
  const [buttonShape, setButtonShape] = useState('pill');
  const [showTextDropdown, setShowTextDropdown] = useState(false);

  // Copy state
  const [snippetCopied, setSnippetCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Load practitioner slug + brand color
  useEffect(() => {
    let done = false;
    const timeout = setTimeout(() => { if (!done) { done = true; setLoading(false); } }, 5000);

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (done) return;
      done = true;
      clearTimeout(timeout);

      if (!user) { setLoading(false); return; }

      try {
        const practDoc = await getDoc(doc(db, 'practitioners', user.uid));
        if (practDoc.exists()) {
          const data = practDoc.data();
          setSlug(data.slug || null);
          // Use practitioner's brand color if available, fall back to gold
          const brandColor = data.website_draft?.colorScheme?.primary
            || data.website_published?.colorScheme?.primary
            || '#bb9749';
          setButtonColor(brandColor);
        }
      } catch (err) {
        console.warn('WidgetConfig: failed to load practitioner:', err.message);
      }
      setLoading(false);
    });

    return () => { done = true; clearTimeout(timeout); unsub(); };
  }, []);

  // Build snippet
  const profileUrl = slug ? `https://divineframeworks.com/site/${slug}` : '';
  const snippet = slug
    ? `<script src="https://divineframeworks.com/widget/book.js"\n  data-practitioner="${slug}"\n  data-color="${buttonColor}"\n  data-text="${buttonText}"\n  data-shape="${buttonShape}"></script>`
    : '';

  // Border radius from shape
  const borderRadiusMap = { pill: '50px', rounded: '8px', square: '0' };
  const borderRadius = borderRadiusMap[buttonShape] || '50px';

  const handleCopy = async (text, setter) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setter(true);
    trackEvent('widget_configured', {});
    setTimeout(() => setter(false), 2000);
  };

  const handleTextSelect = (text) => {
    setButtonText(text);
    setIsCustomText(false);
    setShowTextDropdown(false);
  };

  const handleCustomTextToggle = () => {
    setIsCustomText(true);
    setShowTextDropdown(false);
    setButtonText('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20" style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <div className="max-w-2xl mx-auto px-4 py-6">

        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-6" aria-label="Breadcrumb">
          <Link to="/dashboard/practitioner" className="text-purple-600 hover:text-purple-700 font-medium transition-colors">
            Dashboard
          </Link>
          <span className="text-gray-400">/</span>
          <Link to="/dashboard/settings" className="text-purple-600 hover:text-purple-700 font-medium transition-colors">
            Settings
          </Link>
          <span className="text-gray-400">/</span>
          <span className="text-gray-900 font-medium">Booking Button</span>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#111827', marginBottom: '6px' }}>
            Booking Button for Your Website
          </h1>
          <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.5 }}>
            Add a booking button to your existing website so clients can book sessions directly.
          </p>
        </div>

        {/* ── Customize Section ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-5">Customize</h2>

          {/* Button text */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
              Button text
            </label>
            {isCustomText ? (
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={buttonText}
                  onChange={(e) => setButtonText(e.target.value.slice(0, 30))}
                  placeholder="Enter custom text..."
                  style={{
                    flex: 1, padding: '8px 12px', border: '1.5px solid #D1D5DB',
                    borderRadius: '8px', fontSize: '13px', fontFamily: "'Montserrat', sans-serif",
                    outline: 'none',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = '#7c3aed'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#D1D5DB'; }}
                />
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => { setIsCustomText(false); setButtonText('Book a Session'); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { setIsCustomText(false); setButtonText('Book a Session'); } }}
                  style={{
                    padding: '8px 12px', fontSize: '12px', color: '#6B7280',
                    cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
                    display: 'flex', alignItems: 'center',
                  }}
                >
                  Reset
                </span>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setShowTextDropdown(!showTextDropdown)}
                  onKeyDown={(e) => e.key === 'Enter' && setShowTextDropdown(!showTextDropdown)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px', border: '1.5px solid #D1D5DB',
                    borderRadius: '8px', cursor: 'pointer', userSelect: 'none',
                  }}
                >
                  <span style={{ fontSize: '13px', color: '#374151' }}>{buttonText}</span>
                  <ChevronDown style={{ width: '16px', height: '16px', color: '#9CA3AF' }} />
                </div>
                {showTextDropdown && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px',
                    background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10,
                    overflow: 'hidden',
                  }}>
                    {TEXT_PRESETS.map((text) => (
                      <div
                        key={text}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleTextSelect(text)}
                        onKeyDown={(e) => e.key === 'Enter' && handleTextSelect(text)}
                        style={{
                          padding: '8px 12px', fontSize: '13px', color: '#374151',
                          cursor: 'pointer', userSelect: 'none',
                          background: buttonText === text ? '#f5f3ff' : 'transparent',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f3ff'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = buttonText === text ? '#f5f3ff' : 'transparent'; }}
                      >
                        {text}
                      </div>
                    ))}
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={handleCustomTextToggle}
                      onKeyDown={(e) => e.key === 'Enter' && handleCustomTextToggle()}
                      style={{
                        padding: '8px 12px', fontSize: '13px', color: '#7c3aed',
                        fontWeight: 500, cursor: 'pointer', userSelect: 'none',
                        borderTop: '1px solid #E5E7EB',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f3ff'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      Custom...
                    </div>
                  </div>
                )}
              </div>
            )}
            {isCustomText && (
              <p style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '4px' }}>
                {buttonText.length}/30 characters
              </p>
            )}
          </div>

          {/* Button color */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
              Button color
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="color"
                value={buttonColor}
                onChange={(e) => setButtonColor(e.target.value)}
                style={{
                  width: '36px', height: '36px', border: '2px solid #E5E7EB',
                  borderRadius: '8px', cursor: 'pointer', padding: '2px',
                }}
              />
              <input
                type="text"
                value={buttonColor}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^#[0-9a-fA-F]{0,6}$/.test(val)) setButtonColor(val);
                }}
                style={{
                  width: '90px', padding: '6px 10px', border: '1.5px solid #D1D5DB',
                  borderRadius: '6px', fontSize: '13px', fontFamily: 'monospace',
                }}
              />
            </div>
          </div>

          {/* Button shape */}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
              Button shape
            </label>
            <div style={{ display: 'flex', gap: '12px' }}>
              {[
                { value: 'rounded', label: 'Rounded' },
                { value: 'pill', label: 'Pill' },
                { value: 'square', label: 'Square' },
              ].map((shape) => (
                <label
                  key={shape.value}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    cursor: 'pointer', fontSize: '13px', color: '#374151',
                    userSelect: 'none',
                  }}
                >
                  <input
                    type="radio"
                    name="buttonShape"
                    value={shape.value}
                    checked={buttonShape === shape.value}
                    onChange={() => setButtonShape(shape.value)}
                    style={{ accentColor: '#7c3aed' }}
                  />
                  {shape.label}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* ── Preview Section ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Preview</h2>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '32px 16px', background: '#f9fafb', borderRadius: '8px',
          }}>
            <a
              href={profileUrl || '#'}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.preventDefault()}
              style={{
                display: 'inline-block',
                padding: '14px 32px',
                backgroundColor: buttonColor,
                color: '#ffffff',
                fontFamily: "'Montserrat', 'Helvetica Neue', Arial, sans-serif",
                fontSize: '16px',
                fontWeight: 600,
                textDecoration: 'none',
                borderRadius: borderRadius,
                cursor: 'pointer',
                textAlign: 'center',
                letterSpacing: '0.01em',
                lineHeight: 1,
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                transition: 'opacity 0.2s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
            >
              {buttonText || 'Book a Session'}
            </a>
          </div>
        </div>

        {/* ── Code Snippet Section ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Your code snippet</h2>

          {slug ? (
            <>
              <div style={{
                padding: '14px 16px', background: '#1f2937', borderRadius: '8px',
                marginBottom: '12px', position: 'relative',
              }}>
                <pre style={{
                  fontSize: '12px', color: '#d1d5db', margin: 0,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                  fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
                  lineHeight: 1.6,
                }}>
                  {snippet}
                </pre>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => handleCopy(snippet, setSnippetCopied)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCopy(snippet, setSnippetCopied)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    padding: '8px 18px',
                    background: snippetCopied ? '#D1FAE5' : '#7c3aed',
                    color: snippetCopied ? '#065F46' : 'white',
                    borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                    cursor: 'pointer', userSelect: 'none',
                    transition: 'background 0.2s ease',
                  }}
                >
                  {snippetCopied
                    ? <><Check style={{ width: '14px', height: '14px' }} /> Copied!</>
                    : <><Copy style={{ width: '14px', height: '14px' }} /> Copy code</>}
                </span>
              </div>
            </>
          ) : (
            <p style={{ fontSize: '13px', color: '#9CA3AF', fontStyle: 'italic' }}>
              Set up your profile URL first to generate your code snippet.
            </p>
          )}
        </div>

        {/* ── Direct Link Section ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
          <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '10px' }}>
            Or just link directly:
          </p>
          {profileUrl ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '8px 12px', background: '#f9fafb',
              border: '1px solid #E5E7EB', borderRadius: '6px',
            }}>
              <code style={{
                flex: 1, fontSize: '12px', color: '#374151',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {profileUrl}
              </code>
              <span
                role="button"
                tabIndex={0}
                onClick={() => handleCopy(profileUrl, setLinkCopied)}
                onKeyDown={(e) => e.key === 'Enter' && handleCopy(profileUrl, setLinkCopied)}
                style={{
                  padding: '4px 12px',
                  background: linkCopied ? '#D1FAE5' : '#7c3aed',
                  color: linkCopied ? '#065F46' : 'white',
                  borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                  cursor: 'pointer', userSelect: 'none',
                  transition: 'background 0.2s ease',
                }}
              >
                {linkCopied ? 'Copied!' : 'Copy'}
              </span>
            </div>
          ) : (
            <p style={{ fontSize: '13px', color: '#9CA3AF', fontStyle: 'italic' }}>
              Profile link will be available once your URL is set up.
            </p>
          )}
        </div>

        {/* ── Need Help Section ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 style={{ fontSize: '14px', fontWeight: 700, color: '#111827', marginBottom: '6px' }}>
            Need help?
          </h2>
          <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px', lineHeight: 1.5 }}>
            Step-by-step instructions for Squarespace, Wix, WordPress, Shopify, and other platforms.
          </p>
          <a
            href="/dashboard/settings/support/connecting-your-website"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: '13px', fontWeight: 600, color: '#7c3aed',
              cursor: 'pointer', textDecoration: 'none',
            }}
          >
            View instructions →
          </a>
        </div>

      </div>
    </div>
  );
};

export default WidgetConfig;
